import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../../app/store";
import { plainToInstance } from "class-transformer";
import PortfolioComposite from "../../../../api/models/portfolioComposite";
import { upsertComposite } from "../command-bar/edit-composite-slice";

interface CollapsibleCategoryState {
  compositesExpanded: string[]
  compositeSelected: object | undefined
}

interface CategoryCollapsed {
  id: string,
  collapsed: boolean
}

const initialState: CollapsibleCategoryState = {
  compositesExpanded: [],
  compositeSelected: undefined
};

const collapsibleCategorySlice = createSlice({
  name: 'collapsibleComposite',
  initialState,
  reducers: {
    setCollapsedState(state, action: PayloadAction<CategoryCollapsed>) {
      const stateIsCollapsed = !state.compositesExpanded.some(c => c === action.payload.id)
      if (stateIsCollapsed && !action.payload.collapsed) {
        state.compositesExpanded.push(action.payload.id);
      } else if (!stateIsCollapsed && action.payload.collapsed) {
        delete state.compositesExpanded[state.compositesExpanded.indexOf(action.payload.id)];
      }
    },
    collapseMany(state, action: PayloadAction<string[]>) {
      action.payload.forEach(id => {
        const compositeExpanded = state.compositesExpanded.find(c => c === id);
        if (compositeExpanded) {
          delete state.compositesExpanded[state.compositesExpanded.indexOf(compositeExpanded)];
        }
      })
    },
    expandAll(state, action) {
      state.compositesExpanded = [];
    },
    setSelectedComposite(state, action: PayloadAction<object | undefined>) {
      state.compositeSelected = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(upsertComposite.fulfilled, (state, action) => {
      state.compositeSelected = undefined;
    })
  }
});

// Export the auto-generated action creator with the same name
export const { setCollapsedState, collapseMany, expandAll, setSelectedComposite } = collapsibleCategorySlice.actions;

// Export selectors
export const selectIsCategoryCollapsed = (compositeId: string) => createSelector(
  [
    (state: RootState) => state.collapsibleComposite.compositesExpanded
  ],
  (categoryExpanded) => {
    return !categoryExpanded.includes(compositeId);
  }
)

export const selectPortfolioCompositeSelected = createSelector([(state: RootState) => state.collapsibleComposite.compositeSelected], (compositeSelected) => {
  if (compositeSelected) {
    return plainToInstance(PortfolioComposite, compositeSelected);
  } else {
    return undefined;
  }
})

// Export the generated reducer function
export default collapsibleCategorySlice.reducer;